import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Image,
  Input,
  Modal,
  Popconfirm,
  Radio,
  Row,
  Spin,
  Typography,
  Upload,
  message,
} from "antd";
import Dragger from "antd/es/upload/Dragger";
import {
  DeleteOutlined,
  FilePdfOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import axios from "axios";
import Compressor from "compressorjs";
import dayjs from "dayjs";

import {
  BASE_URL_BEN,
  baseUrl,
  GET_LOAD_IMAGE_NEW_ALL,
  GET_LOAD_IMAGE_NEW_BY,
  HEADERS_EXPORT_BEN,
  HEADERS_UPIMAGE,
  POST_DELETE_IMAGE_NEW,
  POST_UPDATE_PARCEL_STATUS,
  POST_UPLOAD_IMAGE_NEW,
} from "../../../API/apiUrls";
import { PARAM_PUBLIC } from "../../../../utils/constant/StatusConstant";
import CurrencyFormat from "../../../../hook/CurrencyFormat";

const { Text } = Typography;

// ─── Constants ────────────────────────────────────────────────────
const MENU_TYPE = 9;
const IMAGE_TYPE_MAIL = 15;
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const PDF_MIME = "application/pdf";

const CONTRACT_TYPE_LABELS = {
  116: "จดหมายส่งผู้คนค้ำ(116)",
  119: "บอกเลิกสัญญา(119)",
  129: "ค่าบอกเลิกสัญญา(No ems)(129)",
  vsfhp: "สัญญา 2",
  psfhp: "สัญญา 3",
  rpsl: "สัญญา 3(ใหม่)",
  sfhp: "สัญญา 8",
};

const FLAG_STATUS_LABELS = {
  1: "มือ",
  2: "ซื้อรถคืน",
};

const LOCKED_STATUSES = new Set([1, 3]); // status ที่ห้ามแก้ radio

// ─── Helpers ──────────────────────────────────────────────────────
const isPdfFile = (file) =>
  file.type === PDF_MIME || file.name?.toLowerCase().endsWith(".pdf");

const buildImageUrl = (path, token) =>
  `${GET_LOAD_IMAGE_NEW_BY}/${path}?token=${token}`;

const getCompressQuality = (size) => {
  if (size >= 2_500_000) return 0.1;
  if (size >= 2_000_000) return 0.15;
  if (size >= 1_500_000) return 0.2;
  if (size >= 1_000_000) return 0.3;
  return 0.5;
};

const compressImage = (file) =>
  new Promise((resolve, reject) => {
    new Compressor(file, {
      quality: getCompressQuality(file.size),
      success: (result) =>
        resolve(
          new File([result], file.name, {
            type: result.type,
            lastModified: Date.now(),
          }),
        ),
      error: reject,
    });
  });

// ─── ImageGrid: แสดงไฟล์เดิมจาก server ────────────────────────────
const ImageGrid = ({ files, loading, token, onDelete, readOnly = false }) => {
  if (!files?.length) return null;

  return (
    <Spin spinning={loading}>
      <Row gutter={[12, 12]} style={{ marginTop: 8 }}>
        {files.map((file, idx) => {
          const isPdf = file.path?.toLowerCase().endsWith(".pdf");
          const src = buildImageUrl(file.path, token);

          return (
            <Col key={file.path || idx} xs={12} sm={8} md={6}>
              <div style={{ position: "relative" }}>
                {!readOnly && (
                  <Popconfirm
                    title="ยืนยันการลบ"
                    description="ต้องการลบไฟล์นี้?"
                    onConfirm={() => onDelete(file)}
                    okText="ลบ"
                    cancelText="ยกเลิก"
                    okButtonProps={{ danger: true }}
                  >
                    <Button
                      type="primary"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      style={{
                        position: "absolute",
                        top: 6,
                        right: 6,
                        zIndex: 10,
                        borderRadius: 6,
                      }}
                    />
                  </Popconfirm>
                )}

                <Card
                  hoverable
                  style={{ borderRadius: 10, overflow: "hidden" }}
                  styles={{ body: { padding: 0 } }}
                >
                  {isPdf ? (
                    <div
                      onClick={() => window.open(src, "_blank")}
                      style={{
                        height: 120,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        cursor: "pointer",
                        background: "#fff1f0",
                        gap: 6,
                      }}
                    >
                      <FilePdfOutlined
                        style={{ fontSize: 36, color: "#ff4d4f" }}
                      />
                      <Text style={{ fontSize: 11, color: "#cf1322" }}>
                        คลิกเพื่อเปิด PDF
                      </Text>
                    </div>
                  ) : (
                    <Image
                      width="100%"
                      height={120}
                      src={src}
                      style={{ objectFit: "cover", display: "block" }}
                      preview={{ src }}
                    />
                  )}
                </Card>
              </div>
            </Col>
          );
        })}
      </Row>
    </Spin>
  );
};

// ─── UploadSection: ช่อง upload ไฟล์ใหม่ ──────────────────────────
const UploadSection = ({ uploadProps }) => (
  <div
    style={{
      border: "1px solid #1890ff22",
      borderRadius: 12,
      padding: 16,
      background: "#1890ff08",
    }}
  >
    <Dragger
      {...uploadProps}
      style={{ borderRadius: 8, background: "#fafafa" }}
    >
      <InboxOutlined style={{ color: "#1890ff", fontSize: 36 }} />
      <p style={{ margin: "8px 0 4px", fontSize: 14, color: "#555" }}>
        คลิกหรือลากไฟล์มาวางที่นี่
      </p>
      <p style={{ fontSize: 13, color: "#aaa", margin: 0 }}>
        รองรับรูปภาพ และ PDF (ไม่เกิน 5 MB)
      </p>
    </Dragger>
  </div>
);

// ─── Main Modal ───────────────────────────────────────────────────
const UpdateReplyEms = ({ open, close, dataDefault, funcUpdateStatus }) => {
  const token = localStorage.getItem("TOKEN");
  const roleId = parseInt(localStorage.getItem("ROLE_ID"));
  const [, currencyFormatComma, currencyFormatPoint] = CurrencyFormat();

  // ── State ──────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [existingFiles, setExistingFiles] = useState([]); // ไฟล์เดิมจาก server
  const [pendingFiles, setPendingFiles] = useState([]); // ไฟล์รออัปโหลด
  const [fileList, setFileList] = useState([]); // antd Upload UI state

  const loadExistingFiles = useCallback(async () => {
    if (!dataDefault?.id) return;

    setLoading(true);
    try {
      const res = await axios.get(
        `${GET_LOAD_IMAGE_NEW_ALL}/9/${dataDefault.id}`, //ใช้ id ของ user หา
        { headers: HEADERS_UPIMAGE },
      );
      setExistingFiles(res.data || []);
    } catch (err) {
      console.error("loadExistingFiles error:", err);
      message.error("เกิดข้อผิดพลาดในการโหลดรูปภาพ");
    } finally {
      setLoading(false);
    }
  }, [dataDefault?.id]);
  useEffect(() => {
    if (dataDefault?.status > 0) loadExistingFiles();
  }, [dataDefault?.status, loadExistingFiles]);

  // ── Upload flow ────────────────────────────────────────────────
  const beforeUpload = useCallback(async (file) => {
    if (file.size > MAX_FILE_SIZE) {
      message.warning(`ไฟล์ "${file.name}" เกิน 5MB`);
      return Upload.LIST_IGNORE;
    }

    // PDF — ไม่ต้องบีบอัด
    if (isPdfFile(file)) {
      setPendingFiles((prev) => [
        ...prev,
        { file, type: IMAGE_TYPE_MAIL, uid: file.uid },
      ]);
      return false;
    }

    // รูปภาพ — บีบอัดก่อน
    setLoading(true);
    try {
      const compressed = await compressImage(file);
      setPendingFiles((prev) => [
        ...prev,
        { file: compressed, type: IMAGE_TYPE_MAIL, uid: file.uid },
      ]);
    } catch {
      message.error(`ไม่สามารถบีบอัดไฟล์ "${file.name}"`);
    } finally {
      setLoading(false);
    }
    return false;
  }, []);

  const handleFileListChange = useCallback(({ fileList: newList, file }) => {
    setFileList(newList);
    if (file.status === "removed") {
      setPendingFiles((prev) => prev.filter((p) => p.uid !== file.uid));
    }
  }, []);

  const uploadProps = {
    name: "file",
    multiple: true,
    accept: "image/*,.pdf",
    listType: "picture",
    fileList,
    onChange: handleFileListChange,
    beforeUpload,
    iconRender: (file) =>
      isPdfFile(file) ? (
        <FilePdfOutlined style={{ fontSize: 40, color: "#ff4d4f" }} />
      ) : null,
  };

  const uploadPendingFiles = async () => {
    if (pendingFiles.length === 0) return true;

    const fd = new FormData();
    fd.append("id", dataDefault.id);
    fd.append("notis", MENU_TYPE);
    pendingFiles.forEach((item) => {
      fd.append("file", item.file);
      fd.append("menuType", MENU_TYPE);
      fd.append("imageType", item.type);
    });

    const res = await axios.post(POST_UPLOAD_IMAGE_NEW, fd, {
      headers: HEADERS_UPIMAGE,
    });
    return res.status === 200;
  };

  // ── Delete existing file ───────────────────────────────────────
  const handleDeleteFile = async (file) => {
    setLoading(true);
    try {
      const res = await axios.delete(POST_DELETE_IMAGE_NEW, {
        data: [file],
        headers: HEADERS_UPIMAGE,
      });
      if (res.status === 200) {
        setExistingFiles((prev) => prev.filter((f) => f.path !== file.path));
        message.success("ลบไฟล์สำเร็จ");
      }
    } catch (err) {
      console.error("handleDeleteFile error:", err);
      message.error("ลบไฟล์ไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  };

  // ── Submit: upload → sendStatus → close ────────────────────────
  const sendStatus = async (payload) => {
    const res = await axios.post(
      BASE_URL_BEN + POST_UPDATE_PARCEL_STATUS,
      payload,
      { headers: HEADERS_EXPORT_BEN },
    );
    if (res.status !== 200) throw new Error("Status update failed");
    return res.data;
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      // 1) Upload ไฟล์ใหม่ก่อน (ถ้ามี)
      const uploaded = await uploadPendingFiles();
      if (!uploaded) {
        message.error("อัปโหลดไฟล์ไม่สำเร็จ");
        return;
      }

      // 2) Update status
      const payload = {
        id: dataDefault.id,
        parcelNo: values.ems,
        parcelNoResponse: values.emsResponse,
        status: values.radioCus,
      };
      await sendStatus(payload);

      message.success("อัพเดทข้อมูลสำเร็จ");
      funcUpdateStatus?.({ ...dataDefault, status: values.radioCus });
      close(false);
    } catch (err) {
      console.error("onFinish error:", err);
      const status = err?.response?.status;
      message.error(
        status === 404 ? "ไม่พบปลายทาง" : "เกิดข้อผิดพลาดในการอัพเดทข้อมูล",
      );
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = () => {
    message.error("กรุณากรอกข้อมูลที่มีเครื่องหมาย * ให้ครบ");
  };

  // ── Derived values ─────────────────────────────────────────────
  const modalTitle = `ตอบกลับบอกเลิกสัญญา (${
    FLAG_STATUS_LABELS[dataDefault?.flagStatus] ?? "ตามรอบ"
  })`;

  const fullCustomerName =
    dataDefault?.customerFullname &&
    `${dataDefault.customerFullname} ${
      dataDefault.customerTypeID === 0
        ? "(ผู้เช่าซื้อ)"
        : `(ผู้ค้ำที่ ${dataDefault.customerTypeID})`
    }`;

  const isRadioDisabled = LOCKED_STATUSES.has(dataDefault?.status);
  const isAdminRole = roleId === 1;

  return (
    <Modal
      title={modalTitle}
      open={open}
      onCancel={() => close(false)}
      width={750}
      footer={null}
    >
      <Spin spinning={loading} size="large" tip="Loading...">
        <Card>
          <Form
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 24 }}
            layout="horizontal"
            style={{ maxWidth: 600 }}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            initialValues={{
              radioCus: dataDefault?.status || 1,
              ems: dataDefault?.parcelNo || null,
              emsResponse: dataDefault?.parcelNoResponse || null,
            }}
          >
            <Form.Item label="เลขสัญญา">{dataDefault?.contractNo}</Form.Item>
            <Form.Item label="ประเภทสัญญา">
              {CONTRACT_TYPE_LABELS[dataDefault?.contract_schema] ?? "-"}
            </Form.Item>
            <Form.Item label="ประเภทจ่าย">
              {CONTRACT_TYPE_LABELS[dataDefault?.payType] ?? "-"}
            </Form.Item>
            <Form.Item label="ประเภทบัญชี">
              {dataDefault?.accountType}
            </Form.Item>
            <Form.Item label="วันที่ออกหนังสือ">
              {dataDefault?.datetime &&
                dayjs(dataDefault.datetime)
                  .add(543, "year")
                  .format("D MMMM YYYY")}
            </Form.Item>
            <Form.Item label="นำส่ง">{fullCustomerName}</Form.Item>
            <Form.Item label="ที่อยู่">{dataDefault?.ADDR}</Form.Item>
            <Form.Item label="รายละเอียดรถ">
              {`${dataDefault?.brand ?? ""} ${dataDefault?.registerNo ?? ""}`}
            </Form.Item>
            <Form.Item label="ค้างงวด">
              {`${dataDefault?.overdueInstallmentCount || "-"} งวด`}
            </Form.Item>
            <Form.Item label="ยอดเงินค้าง">
              {`${currencyFormatPoint(dataDefault?.overdueInstallmentAmount || 0)} บาท`}
            </Form.Item>
            <Form.Item label="ค่าติดตาม">
              {`${currencyFormatComma(dataDefault?.deptCollectionFees || 0)} บาท`}
            </Form.Item>

            <Form.Item
              label="EMS จดหมาย"
              name="ems"
              rules={[{ required: true, message: "ห้ามว่าง" }]}
            >
              <Input placeholder="ตัวอย่าง: EF582568151TH" maxLength={13} />
            </Form.Item>

            <Form.Item
              label="EMS ใบตอบกลับ"
              name="emsResponse"
              rules={[{ required: true, message: "ห้ามว่าง" }]}
            >
              <Input placeholder="ตัวอย่าง: EF582568151TH" maxLength={13} />
            </Form.Item>

            <Form.Item
              label="การตอบกลับ"
              name="radioCus"
              rules={[{ required: true, message: "โปรดเลือกข้อมูล" }]}
            >
              <Radio.Group disabled={isRadioDisabled}>
                <Radio value={1}>จากใบตอบกลับ</Radio>
                <Radio value={2}>จากเว็บไปษณีย์</Radio>
                <Radio value={3}>ตีกลับ</Radio>
              </Radio.Group>
            </Form.Item>

            {/* Upload ช่องเดียว: ไฟล์ไปรษณีย์ */}
            <Form.Item label="ไฟล์ตอบกลับ">
              <UploadSection uploadProps={uploadProps} />
            </Form.Item>

            {/* ไฟล์เดิมที่บันทึกไว้ */}
            {existingFiles.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <Text type="secondary" style={{ fontSize: 14 }}>
                  ไฟล์ที่บันทึกไว้ ({existingFiles.length} ไฟล์)
                </Text>
                <ImageGrid
                  files={existingFiles}
                  loading={loading}
                  token={token}
                  onDelete={handleDeleteFile}
                  readOnly={!isAdminRole}
                />
              </div>
            )}

            <div style={{ textAlign: "center", marginTop: 16 }}>
              <Button
                danger
                onClick={() => close(false)}
                style={{ marginRight: 20 }}
              >
                ปิด
              </Button>
              <Button type="primary" ghost htmlType="submit">
                บันทึก
              </Button>
            </div>
          </Form>
        </Card>
      </Spin>
    </Modal>
  );
};

export default UpdateReplyEms;
